/**
 * Enhanced validation utilities for the parrot wizard steps
 * Provides comprehensive validation with real-time feedback and specific error messages
 */

/**
 * Validation rules for Step 1 (Basic Configuration)
 */
export const step1ValidationRules = {
  name: {
    required: true,
    requiredMessage: 'Parrot name is required',
    minLength: 2,
    minLengthMessage: 'Parrot name must be at least 2 characters',
    maxLength: 50,
    maxLengthMessage: 'Parrot name must be less than 50 characters',
    pattern: /^[a-zA-Z0-9\s\-_]+$/,
    patternMessage: 'Parrot name can only contain letters, numbers, spaces, hyphens, and underscores',
    custom: (value) => {
      if (value && value.trim() !== value) {
        return 'Parrot name cannot start or end with spaces';
      }
      return null;
    }
  },
  language: {
    required: true,
    requiredMessage: 'Language selection is required',
    custom: (value) => {
      const validLanguages = ['en'];
      if (value && !validLanguages.includes(value)) {
        return 'Selected language is not supported';
      }
      return null;
    }
  },
  max_call_length_seconds: {
    number: true,
    numberMessage: 'Maximum call length must be a valid number',
    min: 30,
    minMessage: 'Maximum call length must be at least 30 seconds',
    max: 3600,
    maxMessage: 'Maximum call length cannot exceed 3600 seconds (1 hour)',
    custom: (value) => {
      if (value !== null && value !== undefined && value !== '' && (value < 30 || value > 3600)) {
        return 'Maximum call length must be between 30 and 3600 seconds, or leave blank for unlimited';
      }
      return null;
    }
  },
  turn_timeout_seconds: {
    required: true,
    requiredMessage: 'Turn timeout is required',
    number: true,
    numberMessage: 'Turn timeout must be a valid number',
    min: 1,
    minMessage: 'Turn timeout must be at least 1 second',
    max: 30,
    maxMessage: 'Turn timeout cannot exceed 30 seconds'
  },
  max_silence_seconds: {
    number: true,
    numberMessage: 'Maximum silence time must be a valid number',
    min: 5,
    minMessage: 'Maximum silence time must be at least 5 seconds',
    max: 120,
    maxMessage: 'Maximum silence time cannot exceed 120 seconds',
    custom: (value) => {
      if (value !== null && value !== undefined && value !== '' && (value < 5 || value > 120)) {
        return 'Maximum silence time must be between 5 and 120 seconds, or leave blank for unlimited';
      }
      return null;
    }
  },
  first_message: {
    required: true,
    requiredMessage: 'First message is required',
    minLength: 10,
    minLengthMessage: 'First message must be at least 10 characters',
    custom: (value) => {
      if (value && value.trim().length < 10) {
        return 'First message must contain at least 10 meaningful characters';
      }
      return null;
    }
  }
};

/**
 * Validation rules for Step 2 (Voice Configuration)
 */
export const step2ValidationRules = {
  voice_id: {
    required: true,
    requiredMessage: 'Voice selection is required',
    custom: (value) => {
      if (value && typeof value !== 'string') {
        return 'Voice ID must be a valid string';
      }
      return null;
    }
  },
  creativity_level: {
    required: true,
    requiredMessage: 'Creativity level selection is required',
    custom: (value) => {
      const validLevels = ['deterministic', 'creative', 'more_creative'];
      if (value && !validLevels.includes(value)) {
        return 'Invalid creativity level selected';
      }
      return null;
    }
  },
  stability: {
    required: true,
    requiredMessage: 'Stability setting is required',
    custom: (value) => {
      const validSettings = ['low', 'medium', 'high'];
      if (value && !validSettings.includes(value)) {
        return 'Invalid stability setting selected';
      }
      return null;
    }
  },
  similarity_boost: {
    required: true,
    requiredMessage: 'Similarity boost setting is required',
    custom: (value) => {
      const validSettings = ['low', 'medium', 'high'];
      if (value && !validSettings.includes(value)) {
        return 'Invalid similarity boost setting selected';
      }
      return null;
    }
  },
  style: {
    required: true,
    requiredMessage: 'Style setting is required',
    custom: (value) => {
      const validSettings = ['low', 'medium', 'high'];
      if (value && !validSettings.includes(value)) {
        return 'Invalid style setting selected';
      }
      return null;
    }
  }
};

/**
 * Validate a single field based on validation rules
 * @param {string} fieldName - Name of the field
 * @param {any} value - Value to validate
 * @param {object} rules - Validation rules
 * @returns {string|null} Error message or null if valid
 */
export const validateField = (fieldName, value, rules) => {
  if (!rules) return null;

  // Handle empty values
  const isEmpty = value === null || value === undefined || value === '';
  
  // Required validation
  if (rules.required && isEmpty) {
    return rules.requiredMessage || `${fieldName} is required`;
  }

  // Skip other validations if value is empty and not required
  if (isEmpty && !rules.required) {
    return null;
  }

  // Convert value to string for string-based validations
  const stringValue = String(value);

  // Min length validation
  if (rules.minLength && stringValue.length < rules.minLength) {
    return rules.minLengthMessage || `${fieldName} must be at least ${rules.minLength} characters`;
  }

  // Max length validation
  if (rules.maxLength && stringValue.length > rules.maxLength) {
    return rules.maxLengthMessage || `${fieldName} must be no more than ${rules.maxLength} characters`;
  }

  // Pattern validation
  if (rules.pattern && !rules.pattern.test(stringValue)) {
    return rules.patternMessage || `${fieldName} format is invalid`;
  }

  // Number validation
  if (rules.number) {
    const numValue = Number(value);
    if (isNaN(numValue)) {
      return rules.numberMessage || `${fieldName} must be a valid number`;
    }

    // Min value validation
    if (rules.min !== undefined && numValue < rules.min) {
      return rules.minMessage || `${fieldName} must be at least ${rules.min}`;
    }

    // Max value validation
    if (rules.max !== undefined && numValue > rules.max) {
      return rules.maxMessage || `${fieldName} must be no more than ${rules.max}`;
    }
  }

  // Custom validation function
  if (rules.custom && typeof rules.custom === 'function') {
    const customError = rules.custom(value, fieldName);
    if (customError) {
      return customError;
    }
  }

  return null;
};

/**
 * Validate an entire form step based on validation rules
 * @param {object} values - Form values
 * @param {object} rules - Validation rules for each field
 * @returns {object} Object with field names as keys and error messages as values
 */
export const validateFormStep = (values, rules) => {
  const errors = {};

  Object.keys(rules).forEach(fieldName => {
    const error = validateField(fieldName, values[fieldName], rules[fieldName]);
    if (error) {
      errors[fieldName] = error;
    }
  });

  return errors;
};

/**
 * Validate Step 1 data
 * @param {object} data - Step 1 form data
 * @returns {object} Validation result with errors and isValid flag
 */
export const validateStep1 = (data) => {
  const errors = validateFormStep(data, step1ValidationRules);
  
  // Additional cross-field validations
  if (data.max_call_length_seconds && data.turn_timeout_seconds) {
    if (data.max_call_length_seconds <= data.turn_timeout_seconds) {
      errors.max_call_length_seconds = 'Maximum call length must be greater than turn timeout';
    }
  }

  if (data.max_silence_seconds && data.turn_timeout_seconds) {
    if (data.max_silence_seconds <= data.turn_timeout_seconds) {
      errors.max_silence_seconds = 'Maximum silence time must be greater than turn timeout';
    }
  }

  return {
    errors,
    isValid: Object.keys(errors).length === 0
  };
};

/**
 * Validate Step 2 data
 * @param {object} data - Step 2 form data
 * @returns {object} Validation result with errors and isValid flag
 */
export const validateStep2 = (data) => {
  const errors = validateFormStep(data, step2ValidationRules);

  return {
    errors,
    isValid: Object.keys(errors).length === 0
  };
};

/**
 * Validate wizard session data for backend submission
 * @param {object} sessionData - Complete wizard session data
 * @returns {object} Validation result with step-specific errors
 */
export const validateWizardSession = (sessionData) => {
  const result = {
    step1: { errors: {}, isValid: true },
    step2: { errors: {}, isValid: true },
    overall: { errors: [], isValid: true }
  };

  // Validate Step 1
  if (sessionData.step1_data) {
    result.step1 = validateStep1(sessionData.step1_data);
  } else {
    result.step1.errors.general = 'Step 1 data is missing';
    result.step1.isValid = false;
  }

  // Validate Step 2
  if (sessionData.step2_data) {
    result.step2 = validateStep2(sessionData.step2_data);
  } else {
    result.step2.errors.general = 'Step 2 data is missing';
    result.step2.isValid = false;
  }

  // Overall validation
  result.overall.isValid = result.step1.isValid && result.step2.isValid;
  
  if (!result.overall.isValid) {
    result.overall.errors.push('Please complete all wizard steps with valid data');
  }

  return result;
};

/**
 * Format validation errors for API responses
 * @param {object} errors - Validation errors object
 * @param {string} step - Which step the errors are from
 * @returns {object} Formatted error response
 */
export const formatValidationErrors = (errors, step) => {
  const fieldErrors = [];
  const generalErrors = [];

  Object.entries(errors).forEach(([field, message]) => {
    if (field === 'general') {
      generalErrors.push(message);
    } else {
      fieldErrors.push({
        field,
        message,
        step
      });
    }
  });

  return {
    code: 'VALIDATION_ERROR',
    message: 'Validation failed',
    step,
    field_errors: fieldErrors,
    general_errors: generalErrors,
    total_errors: fieldErrors.length + generalErrors.length
  };
};

/**
 * Check if a parrot name is unique for a user
 * @param {string} name - Parrot name to check
 * @param {string} userId - User ID
 * @param {string} excludeParrotId - Parrot ID to exclude from check (for editing)
 * @returns {Promise<boolean>} True if name is unique
 */
export const validateParrotNameUniqueness = async (apiClient, name, excludeParrotId = null) => {
  try {
    console.log('🔍 Validating parrot name uniqueness:', { name: name.trim(), excludeParrotId });
    const response = await apiClient.post('/api/parrots/validate-name', {
      name: name.trim(),
      exclude_parrot_id: excludeParrotId
    });
    
    console.log('🔍 Name uniqueness validation response:', response);
    console.log('🔍 Response.isUnique:', response.isUnique);
    console.log('🔍 Response.is_unique:', response.is_unique);
    
    const result = response.isUnique || response.is_unique;
    console.log('🔍 Final result:', result);
    
    return result;
  } catch (error) {
    console.warn('Failed to validate parrot name uniqueness:', error);
    // Return true to allow form submission if validation service fails
    return true;
  }
};

/**
 * Validate Quick Action template selection
 * @param {number} templateId - Template ID to validate
 * @returns {Promise<boolean>} True if template is valid and active
 */
export const validateTemplateSelection = async (apiClient, templateId) => {
  if (!templateId) return true; // Template selection is optional

  try {
    const response = await apiClient.get('/api/quick-action-templates/active');
    const templates = response.templates || response; // Handle both response formats
    return Array.isArray(templates) && templates.some(template => template.id === templateId);
  } catch (error) {
    console.warn('Failed to validate template selection:', error);
    // Return true to allow form submission if validation service fails
    return true;
  }
};

/**
 * Validate voice selection
 * @param {string} voiceId - Voice ID to validate
 * @returns {Promise<boolean>} True if voice is valid and available
 */
export const validateVoiceSelection = async (apiClient, voiceId) => {
  if (!voiceId) return false;

  try {
    const voices = await apiClient.get('/api/voices');
    return voices.some(voice => voice.voice_id === voiceId);
  } catch (error) {
    console.warn('Failed to validate voice selection:', error);
    // Return true to allow form submission if validation service fails
    return true;
  }
};

/**
 * Real-time validation debouncer
 * @param {Function} validationFn - Validation function to debounce
 * @param {number} delay - Debounce delay in milliseconds
 * @returns {Function} Debounced validation function
 */
export const createDebouncedValidator = (validationFn, delay = 300) => {
  let timeoutId;
  
  return (...args) => {
    clearTimeout(timeoutId);
    
    return new Promise((resolve) => {
      timeoutId = setTimeout(async () => {
        try {
          const result = await validationFn(...args);
          resolve(result);
        } catch (error) {
          console.warn('Debounced validation error:', error);
          resolve(null);
        }
      }, delay);
    });
  };
};

/**
 * Validation error codes for consistent error handling
 */
export const ValidationErrorCodes = {
  REQUIRED_FIELD: 'REQUIRED_FIELD',
  INVALID_FORMAT: 'INVALID_FORMAT',
  OUT_OF_RANGE: 'OUT_OF_RANGE',
  DUPLICATE_NAME: 'DUPLICATE_NAME',
  INVALID_SELECTION: 'INVALID_SELECTION',
  TEMPLATE_NOT_FOUND: 'TEMPLATE_NOT_FOUND',
  VOICE_NOT_FOUND: 'VOICE_NOT_FOUND',
  CROSS_FIELD_ERROR: 'CROSS_FIELD_ERROR'
};

/**
 * Get user-friendly error message for validation error codes
 * @param {string} code - Error code
 * @param {object} context - Additional context for the error
 * @returns {string} User-friendly error message
 */
export const getValidationErrorMessage = (code, context = {}) => {
  const messages = {
    [ValidationErrorCodes.REQUIRED_FIELD]: `${context.field || 'This field'} is required`,
    [ValidationErrorCodes.INVALID_FORMAT]: `${context.field || 'This field'} has an invalid format`,
    [ValidationErrorCodes.OUT_OF_RANGE]: `${context.field || 'This field'} is outside the allowed range`,
    [ValidationErrorCodes.DUPLICATE_NAME]: 'A parrot with this name already exists',
    [ValidationErrorCodes.INVALID_SELECTION]: `${context.field || 'Selection'} is not valid`,
    [ValidationErrorCodes.TEMPLATE_NOT_FOUND]: 'Selected template is not available',
    [ValidationErrorCodes.VOICE_NOT_FOUND]: 'Selected voice is not available',
    [ValidationErrorCodes.CROSS_FIELD_ERROR]: context.message || 'Fields have conflicting values'
  };

  return messages[code] || 'Validation error occurred';
};
